import { type ReactNode, useMemo } from 'react';
import type { StorageKeyBuilder } from '../../../context/use-storage-key';
import { deriveDisplayState, isDone } from '../engine/derive-display-state';
import { useItemRegistry, useSettled } from '../engine/use-step-registry';
import type { ChecklistItemProps } from '../types';
import { StatusProbe } from './status-probe';

/** A step the resolver can evaluate — anything carrying an id and a status hook. */
type ResolvableStep = Pick<ChecklistItemProps, 'id' | 'useStatus'>;

type ResolvedSteps<T extends ResolvableStep> = {
  /**
   * Hidden probes that gather the steps' statuses — one per step. The caller MUST render these
   * (they return `null`); without them no status is ever reported and nothing settles.
   */
  probes: ReactNode;
  /**
   * The first step that isn't complete, in order — i.e. the one to do next. `null` until settled,
   * or when every step is complete. Mirrors the `active` step in a {@link Checklist}.
   */
  activeStep: T | null;
  /** Number of steps that are done — completed or skipped. */
  completed: number;
  total: number;
  /** True once every step has reported a status. */
  settled: boolean;
  /** True once settled with every step complete. */
  isComplete: boolean;
};

/**
 * Resolves an ordered list of hook-backed steps down to the next one to do, without rendering any
 * checklist UI. Shares the registry, settle gate, and first-incomplete selection
 * ({@link deriveDisplayState}) with the {@link Checklist} shell, so "which step is active" has a
 * single definition. Statuses are gathered by the returned `probes`, which the caller must render.
 */
export function useResolvedSteps<T extends ResolvableStep>(
  steps: T[],
  storageKeyBuilder?: StorageKeyBuilder,
): ResolvedSteps<T> {
  const { order, statuses, syncItem, removeItem } = useItemRegistry();
  const settled = useSettled(order, statuses);

  const displayStates = useMemo(
    () => deriveDisplayState(order.map((id) => ({ id, status: statuses[id] }))),
    [order, statuses],
  );

  const activeStep = settled
    ? (steps.find((step) => displayStates.get(step.id) === 'active') ?? null)
    : null;

  const total = steps.length;
  const completed = steps.filter((step) => isDone(statuses[step.id])).length;
  const isComplete = settled && total > 0 && completed === total;

  const probes = steps.map((step) => (
    <StatusProbe
      key={step.id}
      id={step.id}
      useStatus={step.useStatus}
      storageKeyBuilder={storageKeyBuilder}
      onStatus={syncItem}
      onUnmount={removeItem}
    />
  ));

  return { probes, activeStep, completed, total, settled, isComplete };
}
